package com.essntl.features.address.ui.component

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.MenuDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.address.domain.model.GooglePlaceModel
import com.essntl.uikit.components.dropdown.EsExposedDropDown
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsSmallIcon
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.collections.forEach
import kotlin.collections.isNotEmpty
import kotlin.text.orEmpty

@Composable
fun AddressDropDown(
    address: AddressModel?,
    selectedPlace: GooglePlaceModel?,
    allPlaces: List<GooglePlaceModel>,
    onGetPlaces: (String) -> Unit,
    onSelectPlace: (GooglePlaceModel) -> Unit,
    onClear: () -> Unit,
) {
    var searchText by remember(address, selectedPlace) {
        mutableStateOf(selectedPlace?.let { "${it.mainText} - ${it.description}" } ?: address?.description.orEmpty())
    }

    var searchJob: Job? by remember { mutableStateOf(null) }
    val scope = rememberCoroutineScope()
    var expanded by remember { mutableStateOf(false) }

    val focusManager = LocalFocusManager.current
    val interactionSource = remember { MutableInteractionSource() }
    val isFocused by interactionSource.collectIsFocusedAsState()

    LaunchedEffect(isFocused) {
        if (!isFocused)
            searchText = selectedPlace?.let { "${it.mainText} - ${it.description}" } ?: address?.description.orEmpty()
    }

    EsExposedDropDown(
        expanded = expanded,
        onExpand = { expanded = !expanded },
        interactionSource = interactionSource,
        value = if (isFocused)
            searchText else
            selectedPlace?.let { "${it.mainText} - ${it.description}" } ?: address?.description.orEmpty(),
        onValueChange = {
            searchJob?.cancel()
            searchJob = scope.launch {
                searchText = it
                delay(500)

                if (searchText.isEmpty())
                    onClear()

                expanded = true

                onGetPlaces(it)
            }
        },
        onClear = onClear,
        placeholder = "Enter address",
        showDropDown = allPlaces.isNotEmpty(),
    ) {
        Row(
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.Top,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(
                text = "Powered by ",
                style = MaterialTheme.esTypography.c1Medium13,
                color = MaterialTheme.esColorScheme.textDisabled,
            )

            Text(
                text = buildAnnotatedString {
                    withStyle(style = SpanStyle(color = Color(0xFF4285F4))) { append("G") }
                    withStyle(style = SpanStyle(color = Color(0xFFEA4335))) { append("o") }
                    withStyle(style = SpanStyle(color = Color(0xFFFBBC05))) { append("o") }
                    withStyle(style = SpanStyle(color = Color(0xFF4285F4))) { append("g") }
                    withStyle(style = SpanStyle(color = Color(0xFF34A853))) { append("l") }
                    withStyle(style = SpanStyle(color = Color(0xFFEA4335))) { append("e") }
                },
                style = MaterialTheme.esTypography.c1Medium13,
            )
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(max = 160.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Column {
                allPlaces.forEach { place ->
                    DropdownMenuItem(
                        text = {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Start,
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                EsSmallIcon(res = EsIcons.Location)

                                Spacer(modifier = Modifier.width(8.dp))

                                Text(
                                    text = buildAnnotatedString {
                                        withStyle(
                                            style = SpanStyle(
                                                color = MaterialTheme.esColorScheme.textPrimary,
                                                fontWeight = FontWeight.Bold,
                                            ),
                                        ) {
                                            append("${place.mainText} ")
                                        }
                                        append(place.secondaryText)
                                    },
                                    style = MaterialTheme.esTypography.p2Medium14,
                                )
                            }
                        },
                        onClick = {
                            onSelectPlace(place)
                            searchText = "${place.mainText} - ${place.description}"
                            focusManager.clearFocus()
                            expanded = false
                        },
                        colors = MenuDefaults.itemColors()
                            .copy(textColor = MaterialTheme.esColorScheme.primary),
                    )
                }
            }
        }
    }
}